<template>
    <component :is="tag" v-bind="linkProps" class="relative flex items-center py-12 px-12 rounded mb-8"
		 :class="{'border': !props.selected, 'cursor-pointer hover:bg-gray-100': !props.disabled, 'cursor-not-allowed opacity-85': props.disabled}"
		 :style="{border: props.selected ? `1px solid ${(props.color ?? $config.public.colors.success)}` : undefined}"
		 @click="handleClick">
        <progress-bar v-if="isLoading" absolute bottom :color="props.color"/>

        <div v-if="props.icon || $slots.icon" class="mr-16">
            <loading-icon v-if="isLoading" :size="props.iconWidth"/>
            <template v-else>
                <Icon v-if="!$slots.icon" :icon="props.icon" :size="props.iconWidth" force-size color="gray-500"/>
                <slot name="icon"/>
            </template>
        </div>
        <div class="flex justify-between flex-grow-x-1 overflow-hidden">
            <div class="flex flex-column justify-center w-full overflow-hidden">
                <span class="text-heading-color text-sm leading-normal truncate">{{ props.title }}</span>
                <div v-if="props.description" class="text-gray-400 text-2xs mt-2 leading-normal" v-html="props.description"/>
				<slot name="description"/>
            </div>
            <lazy-dropdown v-if="$slots.actions" left class="ml-8">
                <btn small icon>
                    <Icon :icon="icons.options"/>
                </btn>
                <template #menu>
                    <lazy-nav-list>
                        <slot name="actions"/>
                    </lazy-nav-list>
                </template>
            </lazy-dropdown>
			<slot name="suffix"/>
        </div>
    </component>
</template>

<script setup lang="ts">
import {optionsIcon} from "../../config";
import {SubmittableComponentEmits, type SubmittableComponentProps, useSubmittableComponent} from "../../composables/useSubmittableComponent";
import {type LinkableProps, useLinkable} from "../../composables/useLinkable";
import {computed} from "vue";

const props = withDefaults(defineProps<SubmittableComponentProps & LinkableProps & {
	title?: string,
	description?: string,
	icon?: string,

	// The method to use for the API request
	method?: string,

	// The width of the displayed icon to prevent CLS when loading
	iconWidth?: number,

	// The loading color
	color?: string,

	// Whether the item shall receive a selected / active state
	selected?: boolean
}>(), {
	method: 'GET',
	iconWidth: 24
});
const emit = defineEmits([...SubmittableComponentEmits, 'click']);

const icons = {
	options: optionsIcon
};

const {submit, isLoading} = useSubmittableComponent(props, emit)
const {linkProps} = useLinkable(props);

const tag = computed(() => {
	if (linkProps.value) {
		return resolveComponent('NuxtLink');
	}

	return 'div';
});

function handleClick(event) {
	if (props.disabled) {
		event.preventDefault();
		return;
	}

	if (!linkProps.value) {
		event.preventDefault();
		submit();
	}

	emit('click', event);
}

defineExpose({
	submit
});

</script>
